import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ATTRIBUTES, POSITIONS, positionAbbrev, positionColor } from "@/lib/playerConstants";
import { playerStats } from "@/lib/training";
import { playerMatchStats, noteColor } from "@/lib/matchStats";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Search, Star, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

const ATTRIBUTE_KEYS = new Set(ATTRIBUTES.map((a) => a.key));

const COLS = [
  { key: "name", label: "Name" },
  { key: "position", label: "Pos." },
  { key: "jahrgang", label: "Jg." },
  { key: "attendance", label: "Anwesenheit" },
  { key: "avgNote", label: "Ø Note" },
  { key: "talent", label: "Talent" },
  { key: "overall", label: "Overall" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState("all");
  const [jahrgangFilter, setJahrgangFilter] = useState("all");
  const [sortKey, setSortKey] = useState("overall");
  const [sortDir, setSortDir] = useState("desc");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, tRes, mRes] = await Promise.all([
        api.get("/players"),
        api.get("/trainings"),
        api.get("/matches"),
      ]);
      setPlayers(pRes.data);
      setTrainings(tRes.data);
      setMatches(mRes.data);
    } catch {
      toast.error("Spieler konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const jahrgaenge = useMemo(() => {
    const s = new Set(players.map((p) => p.jahrgang));
    return Array.from(s).sort((a, b) => b - a);
  }, [players]);

  // Per-player stats (Anwesenheit + Ø Note) — uses existing helpers from PlayerDetail
  const stats = useMemo(() => {
    const m = {};
    for (const p of players) {
      const t = playerStats(p.id, trainings);
      const r = playerMatchStats(p.id, matches);
      m[p.id] = {
        attendanceTotal: t.total,
        attendanceQuote: t.total > 0 ? t.quote : null,
        avgNote: r.seasonAvg,
        ratedCount: r.total,
      };
    }
    return m;
  }, [players, trainings, matches]);

  const filtered = useMemo(() => {
    let list = players;
    if (positionFilter !== "all") list = list.filter((p) => p.position === positionFilter);
    if (jahrgangFilter !== "all") list = list.filter((p) => String(p.jahrgang) === jahrgangFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (p, k) => {
      if (ATTRIBUTE_KEYS.has(k)) return p.attribute[k];
      if (k === "attendance") return stats[p.id]?.attendanceQuote ?? -1;
      if (k === "avgNote") {
        // Lower note is better → invert so "desc" puts the best player on top
        const v = stats[p.id]?.avgNote;
        return v == null ? 999 : v;
      }
      return p[k];
    };
    return [...list].sort((a, b) => {
      const va = getVal(a, sortKey);
      const vb = getVal(b, sortKey);
      if (typeof va === "string") return va.localeCompare(vb) * dir;
      // For avgNote: invert direction (lower note is better)
      if (sortKey === "avgNote") return (va - vb) * (sortDir === "asc" ? -1 : 1);
      return (va - vb) * dir;
    });
  }, [players, positionFilter, jahrgangFilter, search, sortKey, sortDir, stats]);

  // Max value per attribute for visible row highlighting
  const maxPerAttr = useMemo(() => {
    const m = {};
    for (const a of ATTRIBUTES) {
      m[a.key] = filtered.length === 0
        ? -1
        : Math.max(...filtered.map((p) => p.attribute[a.key] ?? 0));
    }
    return m;
  }, [filtered]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ArrowUpDown className="inline h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc"
      ? <ArrowUp className="inline h-3 w-3 ml-1 text-brand-lighter" />
      : <ArrowDown className="inline h-3 w-3 ml-1 text-brand-lighter" />;
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/players/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} gelöscht`);
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const openAttributes = (e, id) => {
    e.stopPropagation();
    navigate(`/spieler/${id}/bearbeiten`, { state: { step: 2 } });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">Dashboard</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100">Spieler</h1>
          <p className="text-zinc-400 text-sm mt-1">
            <span className="text-zinc-100 font-semibold" data-testid="players-count">{players.length}</span> im Kader
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate("/spieler/vergleich")}
            variant="outline"
            className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 rounded-md h-11"
            data-testid="compare-players-btn"
          >
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Spielervergleich
          </Button>
          <Button
            onClick={() => navigate("/spieler/neu")}
            className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11 px-5"
            data-testid="add-player-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Neuer Spieler
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Spieler suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 h-10 rounded-md focus-visible:ring-brand"
            data-testid="search-input"
          />
        </div>
        <Select value={positionFilter} onValueChange={setPositionFilter}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 h-10 rounded-md focus:ring-brand" data-testid="filter-position">
            <SelectValue placeholder="Position filtern" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">Alle Positionen</SelectItem>
            {POSITIONS.map((p) => (
              <SelectItem key={p} value={p}>{positionAbbrev(p)} · {p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={jahrgangFilter} onValueChange={setJahrgangFilter}>
          <SelectTrigger className="bg-zinc-900 border-zinc-800 text-zinc-100 h-10 rounded-md focus:ring-brand" data-testid="filter-jahrgang">
            <SelectValue placeholder="Jahrgang filtern" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">Alle Jahrgänge</SelectItem>
            {jahrgaenge.map((j) => <SelectItem key={j} value={String(j)}>{j}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-x-auto" data-testid="players-table">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              {COLS.map((c) => (
                <TableHead
                  key={c.key}
                  onClick={() => handleSort(c.key)}
                  className={
                    "text-[11px] uppercase tracking-wide text-zinc-400 cursor-pointer select-none hover:text-brand-lighter transition whitespace-nowrap " +
                    (c.key === "name" ? "sticky left-0 z-10 bg-zinc-900/95 backdrop-blur" : "")
                  }
                  data-testid={`sort-${c.key}`}
                >
                  {c.label}<SortIcon k={c.key} />
                </TableHead>
              ))}
              {ATTRIBUTES.map((a) => (
                <TableHead
                  key={a.key}
                  onClick={() => handleSort(a.key)}
                  className="hidden md:table-cell text-[10px] uppercase tracking-wide text-zinc-500 cursor-pointer select-none hover:text-brand-lighter transition whitespace-nowrap"
                  data-testid={`sort-${a.key}`}
                  title={a.label}
                >
                  {a.label.slice(0, 3)}.<SortIcon k={a.key} />
                </TableHead>
              ))}
              <TableHead className="text-[11px] uppercase tracking-wide text-zinc-400 text-right whitespace-nowrap">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLS.length + ATTRIBUTES.length + 1} className="text-center py-12 text-zinc-500">Lädt…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLS.length + ATTRIBUTES.length + 1} className="text-center py-16">
                  <div className="font-display text-xl font-semibold text-zinc-300 mb-1">Noch keine Spieler</div>
                  <div className="text-sm text-zinc-500 mb-4">Lege deinen ersten Spieler an, um zu starten.</div>
                  <Button onClick={() => navigate("/spieler/neu")} className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md" data-testid="empty-add-player-btn">
                    <Plus className="h-4 w-4 mr-2" />Neuer Spieler
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow
                  key={p.id}
                  className="group border-zinc-800 hover:bg-zinc-800/40 cursor-pointer transition"
                  onClick={() => navigate(`/spieler/${p.id}`)}
                  data-testid={`player-row-${p.id}`}
                >
                  <TableCell className="font-medium text-zinc-100 whitespace-nowrap sticky left-0 z-[1] bg-zinc-900 group-hover:bg-zinc-800/40">{p.name}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center justify-center min-w-[44px] px-2 py-0.5 rounded text-[11px] font-bold border border-zinc-800 bg-zinc-900 ${positionColor(p.position)}`} title={p.position}>
                      {positionAbbrev(p.position)}
                    </span>
                  </TableCell><TableCell className="text-zinc-300 tabular-nums">{p.jahrgang}</TableCell>
                  <TableCell
                    className="tabular-nums text-sm whitespace-nowrap"
                    data-testid={`attendance-${p.id}`}
                    title={
                      stats[p.id]?.attendanceTotal
                        ? `${stats[p.id].attendanceTotal} Trainings erfasst`
                        : "Noch keine Trainings erfasst"
                    }
                  >
                    {stats[p.id]?.attendanceQuote == null ? (
                      <span className="text-zinc-600">—</span>
                    ) : (
                      <span className={
                        stats[p.id].attendanceQuote >= 80 ? "text-emerald-400 font-semibold"
                        : stats[p.id].attendanceQuote >= 50 ? "text-amber-400 font-semibold"
                        : "text-rose-400 font-semibold"
                      }>{stats[p.id].attendanceQuote.toFixed(0)} %</span>
                    )}
                  </TableCell>
                  <TableCell
                    className="tabular-nums text-sm whitespace-nowrap"
                    data-testid={`avg-note-${p.id}`}
                    title={
                      stats[p.id]?.ratedCount
                        ? `${stats[p.id].ratedCount} Spielnoten erfasst`
                        : "Noch keine Spielnoten"
                    }
                  >
                    {stats[p.id]?.avgNote == null ? (
                      <span className="text-zinc-600">—</span>
                    ) : (
                      <span className={`font-semibold ${noteColor(stats[p.id].avgNote)}`}>
                        {stats[p.id].avgNote.toFixed(1)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => openAttributes(e, p.id)}
                      className="inline-flex items-center gap-0.5 hover:opacity-80 transition cursor-pointer"
                      data-testid={`talent-cell-${p.id}`}
                      title="Skill & Attribute bearbeiten"
                    >
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`h-3.5 w-3.5 ${i < p.talent ? "fill-brand-lighter text-brand-lighter" : "fill-transparent text-zinc-700"}`} />
                      ))}
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={(e) => openAttributes(e, p.id)}
                      className="font-display text-lg font-bold text-brand-lighter tabular-nums hover:text-white transition"
                      data-testid={`overall-${p.id}`}
                      title="Skill & Attribute bearbeiten"
                    >
                      {p.overall.toFixed(1)}
                    </button>
                  </TableCell>
                  {ATTRIBUTES.map((a) => {
                    const v = p.attribute[a.key];
                    const isMax = v === maxPerAttr[a.key] && v > 0;
                    return (
                      <TableCell
                        key={a.key}
                        className={
                          "hidden md:table-cell text-center tabular-nums text-sm " +
                          (isMax
                            ? "text-brand-lighter font-bold bg-brand/15"
                            : "text-zinc-300")
                        }
                        title={isMax ? `Höchster ${a.label}-Wert` : a.label}
                        data-testid={isMax ? `top-${a.key}-${p.id}` : undefined}
                      >
                        {v}
                      </TableCell>
                    );
                  })}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => navigate(`/spieler/${p.id}/bearbeiten`)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-brand-lighter hover:bg-zinc-800"
                        data-testid={`edit-player-${p.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => setConfirmDelete(p)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-zinc-800"
                        data-testid={`delete-player-${p.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Spieler löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDelete?.name} wird unwiderruflich entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700" data-testid="delete-cancel-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="delete-confirm-btn">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
